import {
  Module,
  getModule,
  VuexModule,
  Action,
  Mutation,
} from "vuex-module-decorators";
import store from "@/store";
import Amplify, { Auth } from "aws-amplify";
import awsExports from "@/aws-exports";
import router from "@/router";
import {
  ForgotPasswordInfo,
  ForGotPasswordSubmitInfo,
  PasswordChangeInfo,
  UserInfo,
} from "@/types/user";
import { CognitoUser } from "amazon-cognito-identity-js";
import accessControlsModule from "@/store/modules/accessControls";
import usersModule from "@/store/modules/users";

Amplify.configure(awsExports);

@Module({ dynamic: true, store, name: "user", namespaced: true })
class User extends VuexModule {
  // state
  private errorMessage = "";
  private loggedIn = false;

  // actions
  @Action({})
  public async signIn(userInfo: UserInfo) {
    try {
      const user: CognitoUser | any = await Auth.signIn(
        userInfo.email,
        userInfo.password
      );
      // First time login
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        if (user.challengeParam.userAttributes["custom:user_group"] != "pwc") {
          router.push("/login");
          const errorMessage = "ログインできません";
          this.SET_ERROR_MESSAGE(errorMessage);
          return;
        }
        const challengeName = user.challengeName;
        router.push({ name: "forcechangepassword", params: { challengeName } });
        return;
      }
      await accessControlsModule.fetchAccessControl("pwc");
      const acl = accessControlsModule.GET_ACCESS_CONTROL;
      if (!acl) {
        router.push("/login");
        const errorMessage = "ログインできません";
        this.SET_ERROR_MESSAGE(errorMessage);
        return;
      }
      const logInPermission = acl?.find(
        (element) => element.FunctionId == "login.pwc"
      );
      const canLogin = logInPermission ? logInPermission.IsPermitted : false;
      if (!canLogin) {
        router.push("/login");
        const errorMessage = "ログインできません";
        this.SET_ERROR_MESSAGE(errorMessage);
        await Auth.signOut();
        this.SET_LOGGEDIN_STATUS();
        return;
      }
      this.SET_LOGGEDIN_STATUS();
      await Auth.currentSession();
      this.SET_ERROR_MESSAGE("");
      router.push("/monitoring");
    } catch (error: any) {
      const errorMessage = await this.ErrorMessageHandling(error.code);
      this.SET_ERROR_MESSAGE(errorMessage);
    }
  }

  @Action({})
  public async updatePassword(userInfo: PasswordChangeInfo) {
    try {
      const user = await Auth.signIn(userInfo.email, userInfo.password);
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        await Auth.completeNewPassword(user, userInfo.newPassword, {});
        router.push("/login");
        return;
      }
      await Auth.changePassword(
        user,
        userInfo.password,
        userInfo.newPassword,
        {}
      );
      await Auth.currentSession();
      router.push("/");
    } catch (e: any) {
      const errorMessage = await this.ErrorMessageHandling(e.code);
      this.SET_ERROR_MESSAGE(errorMessage);
    }
  }

  @Action({})
  public async forgotPassword(userInfo: ForgotPasswordInfo) {
    try {
      await Auth.forgotPassword(userInfo.email);
    } catch (error: any) {
      const errorMessage = await this.ErrorMessageHandling(error.code);
      this.SET_ERROR_MESSAGE(errorMessage);
    }
  }

  @Action({})
  public async forgotPasswordSubmit(userInfo: ForGotPasswordSubmitInfo) {
    try {
      await Auth.forgotPasswordSubmit(
        userInfo.email,
        userInfo.code,
        userInfo.password
      );
      router.push("/login");
    } catch (error: any) {
      const errorMessage = await this.ErrorMessageHandling(error.code);
      this.SET_ERROR_MESSAGE(errorMessage);
    }
  }

  @Action({})
  public async signOut() {
    try {
      await Auth.signOut();
      this.SET_LOGGEDIN_STATUS();
      router.push({ name: "login" });
    } catch (error) {
      // tslint:disable-next-line:no-console
    }
  }

  @Action({})
  public async clearError() {
    this.SET_ERROR_MESSAGE("");
  }

  @Action({})
  public async setError(error: string) {
    this.SET_ERROR_MESSAGE(error);
  }

  @Action({})
  public async checkLoggedIn() {
    try {
      const currentSession = await Auth.currentSession();
      return currentSession;
    } catch (error) {
      // tslint:disable-next-line:no-console
    }
  }

  // mutation
  @Mutation
  private SET_LOGGEDIN_STATUS(): void {
    this.loggedIn = !this.loggedIn;
  }

  @Mutation
  private SET_ERROR_MESSAGE(message: string): void {
    this.errorMessage = message;
  }

  // getters
  get GET_LOGGEDIN_STATUS() {
    return this.loggedIn;
  }

  get GET_ERROR_MESSAGE() {
    return this.errorMessage;
  }

  @Action({})
  public async ErrorMessageHandling(error: string) {
    let message = "エラーが発生されました";
    switch (error) {
      case "NotAuthorizedException":
        message = "メールアドレスまたはパスワードが間違っています。";
        break;
      case "UserNotFoundException":
        message = "入力されたメールアドレスは登録されていません。";
        break;
      case "UsernameExistsException":
        message =
          "このユーザー名は既に使用されています。別のユーザー名を入力してください。";
        break;
      case "UserNotConfirmedException":
        message = "認証コードの入力が完了していません。";
        break;
      case "LimitExceededException":
        message =
          "試行回数の制限を超えました。しばらくしてから試してください。";
        break;
      case "CodeMismatchException":
        message = "入力された認証コードが間違っていました。";
        break;
      case "ExpiredCodeException":
        message = "認証コードが期限切れになっています。";
        break;
      default:
        message = "エラーが発生しました。";
        break;
    }
    return message;
  }
}

export default getModule(User);
